/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { task, timeout } from 'ember-concurrency';
import ApiService from 'cio/services/api';
import config from 'cio/config/environment';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import AccountService from 'cio/services/account';
import Ember from 'ember';
import { webhookUrl } from 'cio/utils/campaigns/webhook-triggered';
import LogModel from 'cio/models/log';
import { action } from '@ember/object';
import { prettyJSONStringify } from 'cio/utils/objects';

interface CampaignsTriggerSetupWebhookArgs {
  campaign: CampaignModel;
  sampleDataContentClasses?: string;
  isCreatingWebhookEndpoint: boolean;
  webhookId: string | null;
  preventWheelEventPropagation?: boolean;
  analyticsSource?: string;
  onDataReceived?: () => unknown;
  pollForWebhooks?: boolean;
}

export default class CampaignsTriggerSetupWebhook extends Component<CampaignsTriggerSetupWebhookArgs> {
  @service account!: AccountService;
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked latestWebhookLog: LogModel | null = null;
  @tracked latestWebhookString: string | null = null;

  @tracked isShowingLearnModal = false;

  get hasLatestWebhook() {
    return this.latestWebhookString != null;
  }

  get webhookUrl() {
    if (this.args.isCreatingWebhookEndpoint) {
      return '';
    }

    return webhookUrl(this.args.webhookId, this.account.current);
  }

  @action
  onCopy() {
    this.analytics.track('campaign', 'webhook_url_copied', {
      model: this.args.campaign,
      source: this.args.analyticsSource,
    });
  }

  @action
  showLearnAboutWebhooksModal() {
    this.isShowingLearnModal = true;
    this.analytics.track('campaign', 'data_workflow_webhooks_modal_viewed');
  }

  @action
  trackLearnAboutWebhooksModalClick(destination: string) {
    this.analytics.track('campaign', 'data_workflow_webhooks_modal_clicked', { destination });
  }

  @action
  trackLearnAboutWebhooksModalVideo() {
    this.analytics.track('campaign', 'data_workflow_webhooks_modal_clicked', { video: true });
  }

  get shouldPollForWebhooks() {
    return (this.args.pollForWebhooks ?? false) || !this.hasLatestWebhook;
  }

  pollForWebhooks = task({ restartable: true }, async () => {
    //eslint-disable-next-line no-constant-condition
    while (true) {
      if (this.shouldPollForWebhooks) {
        await this.fetchWebhooks.perform();
      }

      if (Ember.testing) {
        break;
      }

      await timeout(config.APP.shortPollingDelay);
    }
  });

  fetchWebhooks = task({ drop: true }, async () => {
    let latestLog = await this.args.campaign.fetchLatestWebhookLog(true);

    if (latestLog != null) {
      // Only send the mixpanel when new/different data comes in
      if (this.latestWebhookLog == null || this.latestWebhookLog.id !== latestLog.id) {
        let sources = latestLog.sources.toArray() ?? [];
        this.analytics.track('campaign', 'webhook_test_data_received', {
          model: this.args.campaign,
          source: this.args.analyticsSource,
          logSource: sources.length > 0 ? sources[0] : undefined,
        });
      }

      this.latestWebhookLog = latestLog;
      this.latestWebhookString = prettyJSONStringify(latestLog.attrs);

      this.args.onDataReceived?.();
    }

    return latestLog;
  });
}
